/**
 * PORTFOLIO CONFIGURATION SHEET // KEVIN ARTHUR DE LIMA
 * All portfolio copy, project records, telemetry parameters, and asset paths are maintained here.
 * Edits here propagate to the UI without modifying source templates.
 */

export const config = {
  engineer: {
    name: "KEVIN ARTHUR DE LIMA",
    callsign: "KADL",
    title: "ROBOTICS & EMBEDDED SYSTEMS ENGINEER / UAV SPECIALIST",
    positioning: "I design, build and tune autonomous aircraft, from carbon-fibre airframes and flight-controller firmware to the edge-AI that rides on them.",
    status: "OPEN TO: UAV R&D / ROBOTICS ENGINEER ROLES / INTERNATIONAL RELOCATION OR REMOTE",
    location: "COLOMBO, LK",
    base: "SRI LANKA",
    degree: "BSc (HONS) INFORMATION TECHNOLOGY",
    institution: "Sunway University",
    educationDates: "Jan 2023 to Jan 2026",
    gpa: "3.18",
    focus: "UAV SYSTEMS / EDGE AI / EMBEDDED",
    availability: "OPEN (INTL. RELOCATION / REMOTE)",
    email: "kevinarthurdelima@gmail.com",
    phone: "+94 71 087 7144",
    linkedin: "https://www.linkedin.com/in/kevin-arthur-de-lima-03099920a",
    portfolioUrl: "kadl-portfolio.pages.dev",
    cvPath: "/Kevin-Arthur-De-Lima-CV.pdf",
    rev: "REV 2.0",
    year: "2026"
  },

  model3d: {
    useGlbModel: false,
    glbPath: "/models/airframe.glb",
    targetFps: 60,
    mobileFps: 30,
    maxPixelRatio: 2,
    mobilePixelRatio: 1.5,
    cameraFov: 28,
  },

  telemetry: {
    baseMode: "HOVER",
    maxRpm: 4200,
    battery: "6S",
    voltage: 24.8,
    station: "COLOMBO, LK",
    frequency: "5.8 GHz // ESP-NOW 2.4 GHz",
    linkQuality: "100%"
  },

  sections: [
    { id: "hero", dwg: "DWG-000", title: "AIRFRAME" },
    { id: "about", dwg: "DWG-001", title: "ABOUT" },
    { id: "systems", dwg: "DWG-002", title: "SYSTEMS / SKILLS" },
    { id: "projects", dwg: "DWG-003", title: "AIRFRAMES / PROJECTS" },
    { id: "experience", dwg: "DWG-004", title: "FLIGHT LOG / EXPERIENCE" },
    { id: "education", dwg: "DWG-005", title: "SPECIFICATION / CV" },
    { id: "contact", dwg: "DWG-006", title: "TRANSMISSION / CONTACT" }
  ],

  about: {
    paragraphs: [
      "Robotics and embedded systems engineer focused on autonomous UAVs and heavy-lift propulsion. Engineered a 6.2 kg hybrid-VTOL aircraft running ArduPilot on Navio2, with distributed edge-cloud inference.",
      "Works across the whole stack: carbon-fibre and 3D-printed structures, power systems (6S LiPo), PCB and firmware work, ground stations, and full-stack software (Python, FastAPI, React Native). Has solved real thermal, vibration and control problems on the bench, not just in simulation."
    ],
    table: [
      { key: "BASE", value: "SRI LANKA" },
      { key: "DEGREE", value: "BSc (HONS) INFORMATION TECHNOLOGY" },
      { key: "GPA", value: "3.18" },
      { key: "FOCUS", value: "UAV SYSTEMS / EDGE AI / EMBEDDED" },
      { key: "AVAILABILITY", value: "OPEN (INTL. RELOCATION / REMOTE)" }
    ]
  },

  skills: [
    {
      id: "flight",
      title: "Flight & Autonomy",
      code: "SYS-01",
      partHighlight: "flightController",
      items: [
        "ArduPilot",
        "PX4 (evaluated)",
        "MAVLink",
        "Mission Planner",
        "QGroundControl",
        "Navio2",
        "PID tuning",
        "Blackbox log analysis",
        "optical-flow position hold"
      ]
    },
    {
      id: "embedded",
      title: "Embedded",
      code: "SYS-02",
      partHighlight: "escStack",
      items: [
        "ESP32 (S3 / C3 / CAM)",
        "Raspberry Pi 5",
        "C++",
        "ESP-NOW telemetry",
        "BMI160 IMU",
        "14-bit PWM",
        "sensor filtering"
      ]
    },
    {
      id: "edge-ai",
      title: "Edge AI & Vision",
      code: "SYS-03",
      partHighlight: "companionComputer",
      items: [
        "Python",
        "OpenCV",
        "YOLOv9",
        "Hailo-8L NPU",
        "on-device inference"
      ]
    },
    {
      id: "hardware",
      title: "Structures & Hardware",
      code: "SYS-04",
      partHighlight: "airframeArms",
      items: [
        "Blender",
        "Fusion 360",
        "3D printing (PETG / ABS)",
        "carbon-fibre assembly",
        "soldering",
        "PCB design",
        "LiPo power management (6S)",
        "thermal design"
      ]
    },
    {
      id: "software",
      title: "Software & Cloud",
      code: "SYS-05",
      partHighlight: "payloadBay",
      items: [
        "FastAPI",
        "React",
        "React Native",
        "TypeScript",
        "Docker",
        "PostgreSQL",
        "GraphQL",
        "Neo4j",
        "WebSockets",
        "REST",
        "Linux",
        "Git",
        "AES-256 encryption"
      ]
    }
  ],

  projects: [
    {
      slug: "aeris",
      dwg: "DWG-003-A",
      projectNo: "PRJ-01",
      rev: "REV-4.1",
      title: "Project A.E.R.I.S. (Autonomous Emergency Response & Imaging System)",
      role: "Lead Robotics Engineer",
      status: "STATUS: PROTOTYPE // CAPSTONE RESEARCH",
      configuration: "hybrid-vtol",
      material: "3K CARBON TUBE / 3D PRINTED ABS / PETG",
      mass: "6.2 KG ALL-UP-WEIGHT",
      power: "6S3P 22.2V LIPO + 5V UBEC",
      date: "Sep 2025 to Present",
      heroLine: "Fully autonomous 6.2 kg hybrid-VTOL QuadPlane designed to eliminate 15 to 35 minute dispatch delays in first-responder emergency operations.",
      bullets: [
        "Distributed Dual-Compute Architecture: Pixhawk / Navio2 Linux flight controller paired with onboard Raspberry Pi companion computer over high-speed telemetry link.",
        "Precision Navigation & Obstacle Shield: NEO-M8N GPS navigation coupled with dual VL53L1X Time-of-Flight rangefinders and 3x HC-SR04 ultrasonic array for real-time obstacle avoidance; passive IR beacon tracking for autonomous precision docking.",
        "Thermal Runaway Remediation: Engineered custom ABS Active-Vent motor mounts in Blender, resolving critical 80°C thermal overheating on MN4116 heavy-lift propulsion sets under continuous high-load hover.",
        "Edge-AI & Dispatch Security: Onboard YOLOv9 person detection with sub-10s dispatch-to-insight latency; secured FastAPI backend and React Native emergency trigger app featuring 5s continuous press and 3s abort cancellation buffer to eliminate accidental dispatch."
      ],
      stack: ["ArduPilot", "Navio2 Linux", "Pixhawk", "Raspberry Pi", "YOLOv9", "NEO-M8N GPS", "VL53L1X ToF", "HC-SR04", "MAVLink", "React Native", "FastAPI", "AES-256"],
      image: "/projects/aeris/01.jpg",
      videoUrl: "/projects/aeris/video.mp4",
      paperUrl: "/documents/Project-AERIS-Research-Paper.docx",
      paperTitle: "Project A.E.R.I.S. Capstone Research Paper (Final)"
    },
    {
      slug: "kestrel",
      dwg: "DWG-003-B",
      projectNo: "PRJ-02",
      rev: "REV-2.5",
      title: "Project KESTREL: Facade Edition (Skysweeper UAS)",
      role: "Lead Robotics & Vision Engineer",
      status: "STATUS: PROTOTYPE // FLIGHT TESTED",
      configuration: "hexacopter",
      material: "F550 CARBON-COMPOSITE / ANODIZED ALUM",
      mass: "2.40 KG MTOW (5.28 KG STATIC THRUST // 2.2:1 TWR)",
      power: "3S 11.1V 2200MAH LIPO / DIRECT 12V SOLENOID BUS",
      date: "2025 to 2026",
      heroLine: "Autonomous tethered hexacopter with companion-computer vision-inertial intelligence for high-rise window washing.",
      bullets: [
        "Physical Flight Test Benchmark: Validated on F550 Hexacopter testbed (6x A2212 1400KV + 1045 props + RadioLink F722 INAV); achieved maximum sustainable hover ceiling of 8.0 ft (2.44 m) under continuous 120 PSI pressurized fluid umbilical hose catenary loading.",
        "Subsystem A (30 Hz Vision-Inertial Hold): Pyramidal Lucas-Kanade optical flow on window mullions (center-masked against glass reflections), MPU9250 pinhole gyro de-rotation, forward AJ-SR04M waterproof ultrasonic scale factor, downward VL53L0X ToF altitude hold, and dual-axis PID via UART MultiWii Serial Protocol (MSP_SET_RAW_RC).",
        "Subsystem B (3-5 Hz Perception & RL Control): Multi-spectral heuristic dirt density scorer (Sobel gradient + dark-channel prior, 2.08 ms CPU) with 100% contamination recall and 100% glare/foliage rejection across 70 test conditions; Contextual Linear Q-Actor RL pan nozzle sweep yielding +49.8% water conservation (0.593 L vs 1.182 L) and 52% faster cleaning cycle at 6.52 µs inference with 71.0% compute headroom margin on RPi 5.",
        "Real-Time Failsafe Suite: Hose drag pitch detection (|θ_pitch| > 18°) triggering immediate fluid shut-off and +60µs emergency climb injection; facade proximity violation (<0.25 m) reverse thrust nudge; 150 ms FC UART heartbeat watchdog."
      ],
      stack: ["RadioLink F722", "Raspberry Pi 5 (8GB)", "INAV 9.0.1", "MSP Protocol", "Lucas-Kanade Flow", "MPU9250 IMU", "AJ-SR04M Ultrasonic", "VL53L0X ToF", "Reinforcement Learning", "OpenCV", "Python"],
      image: "/projects/kestrel/01.jpg",
      videoUrl: "/projects/kestrel/video.mp4"
    },
    {
      slug: "prometheus-scout",
      dwg: "DWG-003-C",
      projectNo: "PRJ-03",
      rev: "REV-2.0",
      title: "Prometheus Scout & Smart-Lock Ground Ecosystem",
      role: "Systems Architect & UAV Firmware Engineer",
      status: "STATUS: PROTOTYPE",
      configuration: "hybrid-vtol",
      material: "3K CARBON TUBE / CUSTOM FR4 STACK / 3D PETG / EPO FOAM",
      mass: "3.4 KG (SCOUT) // 180G (MICRO-DRONE)",
      power: "4S2P LI-ION 18650 (SCOUT) // 1S 3.7V LIPO (MICRO)",
      date: "2023 to 2024",
      heroLine: "Edge-AI agricultural survey aircraft with custom C++ ESP32 flight controller and Smart-Lock handheld transmitter.",
      bullets: [
        "\"Dual-brain\" architecture: ArduPilot/PX4 flight stabilisation decoupled from Raspberry Pi 5 + Hailo-8L running YOLOv9 offline for tea and rubber estates.",
        "4+1 twin-boom QuadPlane / hybrid-VTOL airframe evaluated; produces ~2 MB spot-dosing pathology maps with zero 4G dependency.",
        "Custom C++ flight controller on ESP32-S3 with BMI160 IMU, software low-pass vibration filtering, and stacked carbon airframe architecture.",
        "Dual-band telemetry network: ESP-NOW low-latency command link plus Wi-Fi PID parameter tuning.",
        "Custom ground transmitter on ESP32-C3 Super Mini with OLED HUD and weighted-average filtering to eliminate analog joystick noise."
      ],
      stack: ["ArduPilot", "Raspberry Pi 5", "Hailo-8L NPU", "YOLOv9", "ESP32-S3", "ESP32-C3", "C++ Firmware", "BMI160 IMU", "ESP-NOW", "FreeRTOS"],
      image: "/projects/prometheus-scout/01.jpg"
    }
  ],

  softwareProducts: [
    {
      id: "S-01",
      name: "Gym Management System (GMS V1.2/V1.5)",
      status: "STATUS: DEPLOYED",
      oneLine: "Client: Tranzformers.lk Gym. On-premise Docker POS, access control, immutable append-only cash ledger, Hikvision turnstile sync, AES-256-GCM with blind-index search, WhatsApp alerts"
    },
    {
      id: "S-02",
      name: "A.E.G.I.S. (Innowave Engineering)",
      status: "STATUS: DEPLOYED",
      oneLine: "B2B civil-engineering procurement ERP: 6-phase MRF-to-GRN state machine, BOQ quota enforcement, 3-way reconciliation, Zoho Books/Projects integration"
    },
    {
      id: "S-03",
      name: "CafeOS (Prometheus Dynamics)",
      status: "STATUS: DEPLOYED",
      oneLine: "B2B SaaS POS/ERP for cafes: Node.js, Express, Prisma, PostgreSQL, React, Vite, Docker, OCR invoice parsing, recipe inventory with reorder automation, WhatsApp Business alerts & offline-first sync"
    },
    {
      id: "S-04",
      name: "FitSync / Subash Fit Coach",
      status: "STATUS: DEPLOYED",
      oneLine: "Fitness tracking hub, Prisma/PostgreSQL for 200+ concurrent connections"
    },
    {
      id: "S-05",
      name: "HealthyWay Fitness Ecosystem (Web System & Local Station)",
      status: "STATUS: IN CREATION",
      oneLine: "Production web system (Next.js App Router, Supabase Postgres, Ed25519 turnstile passes, Koko/Mintpay BNPL, TOTP MFA) + Avissawella Counter PC autonomous edge server (Windows Service, Hikvision LAN Engine <500ms, Electron+React POS, 48h offline resilience, AES-256-GCM nightly backups)"
    }
  ],

  experience: [
    {
      waypoint: "WP-01",
      alt: "62.0 m",
      role: "Lead Robotics Engineer",
      entity: "Project A.E.R.I.S.",
      period: "Sep 2025 to Present",
      location: "Colombo, LK",
      bullets: [
        "Engineered 6.2 kg autonomous hybrid-VTOL QuadPlane to eliminate 15-35 min emergency response delays.",
        "Integrated ArduPilot on Navio2 Linux with customized PID stabilisation for heavy-lift hover and forward transition.",
        "Built edge YOLOv9 human detection GCS pipeline delivering sub-10s critical target identification.",
        "Engineered custom ABS Active-Vent motor mounts resolving 80°C thermal runaway on MN4116 propulsion sets."
      ]
    },
    {
      waypoint: "WP-02",
      alt: "18.5 m",
      role: "Technical Support & AV Integration Specialist",
      entity: "Seadale Malaysia Sdn. Bhd.",
      period: "Jan 2025 to Apr 2025",
      location: "Kuala Lumpur, MY",
      bullets: [
        "On-site integration of enterprise AV systems for high-profile clients including KWSP and Khazanah Nasional.",
        "Full signal-chain diagnosis across projection systems, multi-zone acoustic arrays, and IoT hardware controllers.",
        "Conducted site inspections, commissioning verifications, and authored technical as-built documentation."
      ]
    },
    {
      waypoint: "WP-03",
      alt: "5.0 m",
      role: "Full Stack Software Engineer",
      entity: "Binary Technologies",
      period: "Sep 2021 to Nov 2022",
      location: "Colombo, LK",
      bullets: [
        "Engineered cloud storage platform utilising GraphQL and Neo4j graph database for hierarchical asset management.",
        "Built low-latency real-time chat application with WebSocket multiplexing.",
        "Transitioned into dedicated frontend role leading UI architecture with Tailwind CSS."
      ]
    },
    {
      waypoint: "WP-04",
      alt: "0.0 m (RUNWAY)",
      role: "Founder / Engineer",
      entity: "Prometheus Dynamics",
      period: "2022 to Present",
      location: "Colombo, LK",
      bullets: [
        "Architected and deployed enterprise software systems (GMS V1.5, A.E.G.I.S. ERP, HealthyWay) and agricultural edge drone POCs.",
        "Engineered hardware-software bridges linking turnstiles, biometric scanners, and off-grid IoT sensors to central management backends."
      ]
    }
  ],

  education: [
    {
      degree: "BSc (Hons) Information Technology",
      institution: "Sunway University",
      period: "Jan 2023 to Jan 2026",
      metric: "GPA 3.18",
      notes: "Focus on UAV systems, distributed computation, and embedded edge computing."
    },
    {
      degree: "MUFY, Computer Science",
      institution: "Monash College",
      period: "Jan 2022 to Oct 2022",
      metric: "COMPLETED",
      notes: "Foundation in computer systems, mathematics, and algorithms."
    },
    {
      degree: "Advanced Diploma in Computer Science (Web Development)",
      institution: "ANC Maya Avenue",
      period: "Jan 2019 to Sep 2021",
      metric: "DISTINCTION",
      notes: "Full-stack web architecture, database design, and software lifecycle."
    }
  ],

  recommendation: {
    title: "Official Recommendation Letter",
    dwg: "DWG-REC-01",
    author: "Professor Ir Ts. Yap Kian Meng",
    role: "Head of Research Centre For Human-Machine Collaboration (HUMAC)",
    faculty: "Faculty of Engineering and Technology, Sunway University",
    email: "kmyap@sunway.edu.my",
    project: "Project A.E.R.I.S. (Autonomous Emergency Response System)",
    image: "/documents/sunway-university-recommendation-letter.jpg",
    summary: "Supervised capstone engineering on Project A.E.R.I.S. Validated 6.2kg Hybrid-VTOL ArduPilot flight stack, custom Active-Vent motor mounts (ABS), and edge YOLOv9 detection GCS pipeline.",
    quote: "As the Lead Robotics Engineer for this project, Kevin consistently demonstrated exceptional technical proficiency and problem-solving skills. He successfully engineered a 6.2kg Hybrid-VTOL autonomous aircraft using ArduPilot (Navio2) on Linux... His ability to combine strong hardware prototyping skills with full-stack software expertise enables him to deliver comprehensive, end-to-end robotics solutions."
  },

  contact: {
    headline: "If you build drones and need someone who can also write the software, talk to me.",
    subtext: "Available for UAV R&D, robotics hardware engineering, and autonomous systems development. Worldwide relocation or remote.",
    email: "kevinarthurdelima@gmail.com",
    phone: "+94 71 087 7144",
    linkedin: "https://www.linkedin.com/in/kevin-arthur-de-lima-03099920a",
    portfolio: "kadl-portfolio.pages.dev",
    footerText: "KEVIN ARTHUR DE LIMA / ENGINEERED 2026 / REV 2.0"
  }
};
